import { useRef } from 'react';
import useSWR from 'swr';
import type { Agent } from '../agent';
import { getAreaSearchParams } from '../area';
import { withSubVertical } from '../subVertical';
import { useSearchParams } from './useSearchParams';

type AreaContent = {
    agents: Agent[];
    squareMeterPrice: number | null;
};

type AreaContentState = AreaContent & {
    isError: boolean;
};

const fetcher = async (url: string): Promise<AreaContent> => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Server responded with status ${response.status}`);
    const data = await response.json();
    if (!Array.isArray(data?.agents)) throw new Error('Malformed area content response');
    return {
        agents: data.agents as Agent[],
        squareMeterPrice: typeof data?.squareMeterPrice === 'number' ? data.squareMeterPrice : null,
    };
};

export function useAreaContent(
    areaContentUrl: string,
    initialContent: AreaContent,
    serverFetchFailed: boolean,
    subVertical: string,
): AreaContentState {
    const searchParams = useSearchParams();
    // Keyed on the area alone, matching what the server sends on to /sold-in-area, so that narrowing
    // the search without moving the map does not refetch a result that cannot have changed.
    const key = `${areaContentUrl}?${withSubVertical(getAreaSearchParams(searchParams), subVertical).toString()}`;
    const serverRenderedKey = useRef(key);
    const isServerRenderedKey = key === serverRenderedKey.current;

    // A failed server fetch left no order to preserve, and the podlet renders nothing on an empty
    // list, so without a retry one slow upstream response would hide it for the whole page session.
    const trustServerContent = isServerRenderedKey && !serverFetchFailed;

    const { data, error } = useSWR(key, fetcher, {
        // The server already fetched this exact content, and the agent list is ordered randomly, so
        // revalidating would reshuffle the carousel a moment after it appears. Fetch only for an
        // area change (a new key) or to retry a failed server request.
        ...(trustServerContent ? { fallbackData: initialContent, revalidateOnMount: false } : {}),
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        // The first request after a failed server fetch is already the retry. If it also fails,
        // leave the podlet hidden instead of repeatedly calling the profiles API during an outage.
        shouldRetryOnError: false,
    });

    return {
        agents: data?.agents ?? [],
        squareMeterPrice: data?.squareMeterPrice ?? null,
        isError: Boolean(error),
    };
}
